// Care plan editor — one client's plan: goals, care task list, caregiver
// skills checklist, signatures and the draft → active lifecycle.
//
// Rules (GAPP §914, §916, §918):
//   • goals and tasks are edited on a draft; an active plan only takes goal
//     progress and skills updates — anything else is a new version;
//   • the RN signs before activation; the caregiver (family / CNA) signs too;
//   • activation replaces the client's task list, which the visit trigger
//     copies onto every visit so the caregiver app checklist follows the plan;
//   • the skills checklist records demonstrated → return demo → with
//     assistance → independent dates per caregiver per procedure.
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { useAuth } from '../../hooks/useAuth';
import {
  FREQUENCIES, GOAL_KINDS, GOAL_STATUSES, CAREGIVER_ROLES, DAYS,
  getTaskCatalog, getClientPlans, getPlanDetail, draftPlan, updatePlan, signPlan, activatePlan, reviewPlan, archivePlan,
  upsertGoal, deleteGoal, upsertPlanTask, deletePlanTask, upsertSkill, deleteSkill, getFieldStaffForSkills, skillStage,
} from '../../lib/carePlans';

const field = { width: '100%', padding: '6px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: 12, fontFamily: 'inherit' };
const label = { fontSize: 10, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 3 };
const section = { fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--accent)', margin: '18px 0 8px', paddingBottom: 4, borderBottom: '1px solid var(--border)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' };
const th = { textAlign: 'left', fontSize: 10, color: 'var(--text3)', fontWeight: 500, padding: '5px 6px', borderBottom: '1px solid var(--border)', textTransform: 'uppercase', letterSpacing: '0.05em' };
const td = { padding: '4px 6px', borderBottom: '0.5px solid var(--border)', verticalAlign: 'top', fontSize: 12 };
const fmtD = (d) => d ? format(new Date(`${String(d).slice(0, 10)}T12:00:00`), 'MMM d, yyyy') : '—';
const fmtDT = (d) => d ? format(new Date(d), 'MMM d, yyyy h:mm a') : '—';
const STATUS_TONE = { draft: 'var(--warn)', active: 'var(--success)', superseded: 'var(--text3)', archived: 'var(--text3)' };

export default function CarePlanEditor({ client, onChanged }) {
  const navigate = useNavigate();
  const { profile } = useAuth();
  const [plans, setPlans] = useState([]);
  const [plan, setPlan] = useState(null);
  const [detail, setDetail] = useState({ goals: [], tasks: [], skills: [] });
  const [catalog, setCatalog] = useState([]);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [addTask, setAddTask] = useState('');
  const [cgName, setCgName] = useState('');
  const [cgRole, setCgRole] = useState('family');
  const [reviewNote, setReviewNote] = useState('');
  const [reviewDue, setReviewDue] = useState('');

  const say = (m) => { setToast(m); setTimeout(() => setToast(''), 3500); };
  const fail = (e) => setError(e?.message || String(e));

  const load = useCallback(async (preferId) => {
    setLoading(true); setError('');
    const [{ data: ps, error: e1 }, { data: cat }, { data: st }] = await Promise.all([getClientPlans(client.id), getTaskCatalog(), getFieldStaffForSkills()]);
    if (e1) { fail(e1); setLoading(false); return; }
    setPlans(ps || []); setCatalog(cat || []); setStaff(st || []);
    const pick = (ps || []).find(p => p.id === preferId) || (ps || []).find(p => p.status === 'draft') || (ps || []).find(p => p.status === 'active') || (ps || [])[0] || null;
    setPlan(pick);
    setDetail(pick ? await getPlanDetail(pick) : { goals: [], tasks: [], skills: [] });
    setLoading(false);
  }, [client.id]);
  useEffect(() => { load(); }, [load]);

  const reloadDetail = async () => { if (plan) setDetail(await getPlanDetail(plan)); onChanged && onChanged(); };
  const switchPlan = async (id) => { const p = plans.find(x => x.id === id); setPlan(p); setDetail(p ? await getPlanDetail(p) : { goals: [], tasks: [], skills: [] }); };

  const isDraft = plan?.status === 'draft';
  const isActive = plan?.status === 'active';
  // §916 treats the RN signature as the point the plan becomes the agency's
  // clinical commitment, so it is the signature that locks it, not activation.
  // Progress, the skills checklist and the review still move afterwards.
  const signed = !!plan?.rn_signed_at;
  const editable = isDraft && !signed;
  const hasDraft = plans.some(p => p.status === 'draft');

  // ── Actions ────────────────────────────────────────────────────────────────
  const run = async (key, fn, okMsg) => {
    setBusy(key); setError('');
    try { const r = await fn(); if (okMsg) say(typeof okMsg === 'function' ? okMsg(r) : okMsg); return r; }
    catch (e) { fail(e); }
    finally { setBusy(''); }
  };
  const onDraft = () => run('draft', async () => { const id = await draftPlan(client.id); await load(id); onChanged && onChanged(); }, 'Draft created.');
  const onSignRn = () => run('sign', async () => { await signPlan(plan.id, 'rn'); await load(plan.id); }, 'Signed as RN.');
  const onSignCg = () => run('signcg', async () => { await signPlan(plan.id, 'caregiver', cgName, cgRole); setCgName(''); await load(plan.id); }, 'Caregiver signature recorded.');
  const onActivate = () => {
    if (!window.confirm('Activate this care plan? The previous version is superseded and the task list goes to every upcoming visit in the caregiver app.')) return;
    run('activate', async () => { const r = await activatePlan(plan.id); await load(plan.id); onChanged && onChanged(); return r; }, r => `Plan active. ${r.tasks} tasks on the client's list, ${r.visit_tasks_added} added to upcoming visits.`);
  };
  const onReview = () => run('review', async () => { await reviewPlan(plan.id, reviewDue || null, reviewNote); setReviewNote(''); setReviewDue(''); await load(plan.id); onChanged && onChanged(); }, 'Review recorded.');
  const onArchive = () => { if (!window.confirm('Discard this draft?')) return; run('archive', async () => { await archivePlan(plan.id); await load(); onChanged && onChanged(); }, 'Draft discarded.'); };
  const onHeader = (patch) => run('hdr', async () => { const { error: e } = await updatePlan(plan.id, patch); if (e) throw e; setPlan(p => ({ ...p, ...patch })); });

  // Goals
  const saveGoal = (g) => run(`g${g.id || 'new'}`, async () => { const { error: e } = await upsertGoal(g); if (e) throw e; await reloadDetail(); });
  const removeGoal = (id) => run(`gd${id}`, async () => { const { error: e } = await deleteGoal(id); if (e) throw e; await reloadDetail(); });
  const addGoal = (kind) => saveGoal({ care_plan_id: plan.id, kind, domain: '', goal: '', interventions: '', frequency_target: '', sort_order: detail.goals.length + 1 });

  // Tasks
  const savePlanTask = (t) => run(`t${t.id || 'new'}`, async () => { const { error: e } = await upsertPlanTask(t); if (e) throw e; await reloadDetail(); });
  const removePlanTask = (id) => run(`td${id}`, async () => { const { error: e } = await deletePlanTask(id); if (e) throw e; await reloadDetail(); });
  const onAddTask = () => {
    const t = catalog.find(x => x.id === addTask); if (!t) return;
    savePlanTask({ care_plan_id: plan.id, task_id: t.id, frequency: t.default_frequency || 'every_visit', required: !!t.requires_competency, instructions: null, sort_order: t.sort_order || 100 });
    setAddTask('');
  };

  // Skills
  const saveSkill = (s) => run(`s${s.id || 'new'}`, async () => { const { staff: _s, ...row } = s; const { error: e } = await upsertSkill(row); if (e) throw e; await reloadDetail(); });
  const removeSkill = (id) => run(`sd${id}`, async () => { const { error: e } = await deleteSkill(id); if (e) throw e; await reloadDetail(); });
  const addSkillFromTask = (t) => saveSkill({ client_id: client.id, care_plan_id: plan.id, task_id: t.task_id, procedure: t.task?.name || '', caregiver_kind: 'family', caregiver_name: client.responsible_person_name || null });
  const addSkillBlank = () => saveSkill({ client_id: client.id, care_plan_id: plan.id, procedure: 'New procedure', caregiver_kind: 'family', caregiver_name: client.responsible_person_name || null });
  const verifySkill = (s) => saveSkill({ ...s, verified_by: profile?.id || null, verified_at: new Date().toISOString() });

  const availableTasks = useMemo(() => catalog.filter(c => !detail.tasks.some(t => t.task_id === c.id)), [catalog, detail.tasks]);
  const uncoveredRequired = useMemo(() => detail.tasks.filter(t => t.task?.requires_competency && !detail.skills.some(s => s.task_id === t.task_id && s.independent_on)), [detail]);

  if (loading) return <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>;

  return (
    <div>
      {toast && <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--success)', marginBottom: 10 }}>✓ {toast}</div>}
      {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--danger)', marginBottom: 10 }}>{error}</div>}

      {/* Header: version picker + lifecycle */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10 }}>
        {plans.length > 0 && (
          <select style={{ ...field, width: 'auto' }} value={plan?.id || ''} onChange={e => switchPlan(e.target.value)}>
            {plans.map(p => <option key={p.id} value={p.id}>v{p.version_number} · {p.status}{p.effective_date ? ` · ${fmtD(p.effective_date)}` : ''}</option>)}
          </select>
        )}
        {plan && <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 20, background: 'var(--bg2)', color: STATUS_TONE[plan.status] || 'var(--text3)', border: '1px solid var(--border)' }}>{plan.status}</span>}
        <span style={{ flex: 1 }} />
        {!hasDraft && <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={onDraft}>{plans.length ? '+ New version' : '+ Draft from assessment'}</button>}
        {isDraft && <button className="btn btn-outline btn-sm" style={{ color: 'var(--danger)' }} disabled={!!busy} onClick={onArchive}>Discard draft</button>}
        {isDraft && <button className="btn btn-primary btn-sm" disabled={!!busy || !plan.rn_signed_at} title={plan.rn_signed_at ? '' : 'RN signature required first'} onClick={onActivate}>{busy === 'activate' ? 'Activating…' : 'Activate plan'}</button>}
        <button className="btn btn-outline btn-sm" onClick={() => navigate(`/clients/${client.id}/assessment`)}>Assessment</button>
      </div>

      {!plan ? (
        <div style={{ fontSize: 12, color: 'var(--text3)', padding: '14px 0' }}>
          No care plan yet. "Draft from assessment" builds one from the latest nurse assessment: goals from its grids, care tasks from the ADL and skilled-intervention checklists, and the competency audit as the skills checklist.
        </div>
      ) : (
        <>
          {/* Plan facts */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))', gap: 10 }}>
            <div><div style={label}>Effective</div><input type="date" style={field} value={plan.effective_date || ''} disabled={!editable} onChange={e => onHeader({ effective_date: e.target.value || null })} /></div>
            <div><div style={label}>PA period ends</div><input type="date" style={field} value={plan.pa_period_end || ''} disabled={!editable} onChange={e => onHeader({ pa_period_end: e.target.value || null })} /></div>
            <div><div style={label}>Review due</div><input type="date" style={field} value={plan.review_due || ''} disabled={!editable && !isActive} onChange={e => onHeader({ review_due: e.target.value || null })} /></div>
            <div><div style={label}>PPOT signed by physician</div><input type="date" style={field} value={plan.ppot_signed_on || ''} disabled={!editable && !isActive} onChange={e => onHeader({ ppot_signed_on: e.target.value || null })} /></div>
            <div><div style={label}>Last reviewed</div><div style={{ fontSize: 12, padding: '6px 0' }}>{fmtDT(plan.reviewed_at)}</div></div>
          </div>
          <div style={{ marginTop: 10 }}>
            <div style={label}>Plan summary / nursing notes</div>
            <textarea style={{ ...field, minHeight: 56 }} defaultValue={plan.summary || ''} disabled={!editable && !isActive} onBlur={e => e.target.value !== (plan.summary || '') && onHeader({ summary: e.target.value })} />
          </div>

          {/* Goals */}
          {GOAL_KINDS.map(([kind, kindLabel]) => {
            const rows = detail.goals.filter(g => g.kind === kind);
            return (
              <div key={kind}>
                <div style={section}><span>{kindLabel} goals</span>{editable && <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => addGoal(kind)}>+ Goal</button>}</div>
                {rows.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>None.</div> : (
                  <div style={{ overflowX: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                      <thead><tr><th style={th}>{kind === 'teaching' ? 'Objective' : 'Domain'}</th><th style={th}>{kind === 'teaching' ? 'Method & content' : 'Measurable goal'}</th><th style={th}>{kind === 'teaching' ? 'Completion target' : 'Interventions / tasks'}</th><th style={{ ...th, width: 110 }}>{kind === 'teaching' ? 'Status' : 'Frequency / target'}</th><th style={{ ...th, width: 120 }}>Progress</th>{editable && <th style={th} />}</tr></thead>
                      <tbody>
                        {rows.map(g => <GoalRow key={g.id} g={g} kind={kind} editable={editable} active={isActive} onSave={saveGoal} onDelete={removeGoal} />)}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            );
          })}

          {/* Care task list */}
          <div style={section}>
            <span>Care task list (daily service record)</span>
            {editable && (
              <span style={{ display: 'flex', gap: 6 }}>
                <select style={{ ...field, width: 260, padding: '3px 6px' }} value={addTask} onChange={e => setAddTask(e.target.value)}>
                  <option value="">Add a task…</option>
                  {[...new Set(availableTasks.map(t => t.category || 'Other'))].map(cat => (
                    <optgroup key={cat} label={cat}>{availableTasks.filter(t => (t.category || 'Other') === cat).map(t => <option key={t.id} value={t.id}>{t.name}</option>)}</optgroup>
                  ))}
                </select>
                <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} disabled={!addTask} onClick={onAddTask}>Add</button>
              </span>
            )}
          </div>
          {detail.tasks.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>No tasks. Caregivers see nothing on their visit checklist until tasks are added and the plan is activated.</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 640 }}>
                <thead><tr><th style={th}>Task</th><th style={{ ...th, width: 80 }}>Who</th><th style={{ ...th, width: 150 }}>Frequency</th><th style={{ ...th, width: 60 }}>Required</th><th style={th}>Instructions for this client</th>{editable && <th style={th} />}</tr></thead>
                <tbody>{detail.tasks.map(t => <TaskRow key={t.id} t={t} editable={editable} onSave={savePlanTask} onDelete={removePlanTask} />)}</tbody>
              </table>
            </div>
          )}

          {/* Skills checklist */}
          <div style={section}>
            <span>Teaching plan · caregiver skills checklist</span>
            <span style={{ display: 'flex', gap: 6 }}>
              {uncoveredRequired.length > 0 && <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={() => uncoveredRequired.forEach(addSkillFromTask)} title="One row per plan task that needs a verified skill and has none">+ From tasks ({uncoveredRequired.length})</button>}
              <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} onClick={addSkillBlank}>+ Row</button>
            </span>
          </div>
          {detail.skills.length === 0 ? <div style={{ fontSize: 12, color: 'var(--text3)', fontStyle: 'italic' }}>No procedures listed yet.</div> : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 900 }}>
                <thead><tr><th style={th}>Procedure</th><th style={{ ...th, width: 190 }}>Caregiver</th><th style={{ ...th, width: 105 }}>Demonstrated</th><th style={{ ...th, width: 105 }}>Return demo</th><th style={{ ...th, width: 105 }}>With assistance</th><th style={{ ...th, width: 105 }}>Independent</th><th style={{ ...th, width: 120 }}>Verified</th><th style={th}>Notes</th><th style={th} /></tr></thead>
                <tbody>{detail.skills.map(s => <SkillRow key={s.id} s={s} staff={staff} onSave={saveSkill} onDelete={removeSkill} onVerify={verifySkill} />)}</tbody>
              </table>
            </div>
          )}

          {signed && (
            <div style={{ background: 'var(--warn-bg)', border: '0.5px solid var(--warn-border)', borderRadius: 8, padding: '10px 14px', margin: '14px 0', fontSize: 12.5, color: 'var(--text2)' }}>
              <b>Signed on {fmtDT(plan.rn_signed_at)} and locked.</b> The goals, the care task list and the
              summary can no longer be changed. Record progress against a goal here, adjust the days and
              wording on the task list below, or create a new version to change the plan itself.
            </div>
          )}

          {/* Signatures */}
          <div style={section}><span>Signatures (GAPP §916)</span></div>
          <div style={{ fontSize: 11.5, color: 'var(--text3)', margin: '-2px 0 8px' }}>
            Only a Registered Nurse is authorised to sign the provider plan. Signing locks it; changing it
            afterwards means a new version.
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
              <div style={label}>Provider RN</div>
              {plan.rn_signed_at ? <div style={{ fontSize: 12 }}>{plan.rn_signed_name} · {fmtDT(plan.rn_signed_at)}</div>
                : <button className="btn btn-primary btn-sm" disabled={!!busy || (!isDraft && !isActive)} onClick={onSignRn}>Sign as {profile?.full_name || 'RN'}</button>}
            </div>
            <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 10 }}>
              <div style={label}>Caregiver (family / CNA)</div>
              {plan.caregiver_signed_at ? <div style={{ fontSize: 12 }}>{plan.caregiver_signed_name} ({plan.caregiver_signed_role}) · {fmtDT(plan.caregiver_signed_at)}</div> : (
                <div style={{ display: 'flex', gap: 6 }}>
                  <input style={field} placeholder="Caregiver name" value={cgName} onChange={e => setCgName(e.target.value)} />
                  <select style={{ ...field, width: 130 }} value={cgRole} onChange={e => setCgRole(e.target.value)}>{CAREGIVER_ROLES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
                  <button className="btn btn-outline btn-sm" disabled={!!busy || !cgName.trim()} onClick={onSignCg}>Record</button>
                </div>
              )}
            </div>
          </div>

          {/* Review */}
          {isActive && (
            <>
              <div style={section}><span>Review</span></div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 240 }}><div style={label}>Review note</div><input style={field} value={reviewNote} onChange={e => setReviewNote(e.target.value)} placeholder="Reviewed with caregiver; no changes" /></div>
                <div><div style={label}>Next review due</div><input type="date" style={field} value={reviewDue} onChange={e => setReviewDue(e.target.value)} /></div>
                <button className="btn btn-outline btn-sm" disabled={!!busy} onClick={onReview}>Mark reviewed</button>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>Reviewed at every supervisory visit and at least every renewal period. To change goals or tasks, create a new version.</div>
            </>
          )}
        </>
      )}
    </div>
  );
}

function GoalRow({ g, kind, editable, active, onSave, onDelete }) {
  const [row, setRow] = useState(g);
  useEffect(() => setRow(g), [g]);
  const blur = () => { if (JSON.stringify(row) !== JSON.stringify(g)) onSave(row); };
  const inp = (k) => <input style={field} value={row[k] || ''} disabled={!editable} onChange={e => setRow(r => ({ ...r, [k]: e.target.value }))} onBlur={blur} />;
  return (
    <tr>
      <td style={td}>{inp('domain')}</td>
      <td style={td}>{kind === 'teaching' ? inp('goal') : inp('goal')}</td>
      <td style={td}>{inp('interventions')}</td>
      <td style={td}>{kind === 'teaching'
        ? <select style={field} value={row.status} disabled={!editable && !active} onChange={e => onSave({ ...row, status: e.target.value })}>{GOAL_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        : inp('frequency_target')}</td>
      <td style={td}>
        {kind !== 'teaching' && <select style={field} value={row.status} disabled={!editable && !active} onChange={e => onSave({ ...row, status: e.target.value })}>{GOAL_STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>}
        <input style={{ ...field, marginTop: 3 }} placeholder="progress note" value={row.progress_note || ''} disabled={!editable && !active} onChange={e => setRow(r => ({ ...r, progress_note: e.target.value }))} onBlur={blur} />
      </td>
      {editable && <td style={td}><button className="btn btn-ghost btn-sm" onClick={() => onDelete(g.id)} title="Remove">✕</button></td>}
    </tr>
  );
}

function TaskRow({ t, editable, onSave, onDelete }) {
  const [row, setRow] = useState(t);
  useEffect(() => setRow(t), [t]);
  const save = (patch) => { const next = { ...row, ...patch }; setRow(next); const { task: _t, ...clean } = next; onSave(clean); };
  const toggleDay = (d) => { const days = new Set(row.days_of_week || []); days.has(d) ? days.delete(d) : days.add(d); save({ days_of_week: [...days].sort() }); };
  return (
    <tr>
      <td style={td}><div style={{ fontWeight: 500 }}>{t.task?.name}</div><div style={{ fontSize: 10, color: 'var(--text3)' }}>{t.task?.category}{t.task?.instructions ? ` · ${t.task.instructions}` : ''}</div></td>
      <td style={td}><span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 10, background: t.task?.skill_kind === 'skilled' ? 'var(--accent-bg)' : 'var(--copper-bg)', color: 'var(--text2)' }}>{t.task?.skill_kind === 'skilled' ? 'Nurse' : t.task?.skill_kind === 'pss' ? 'Aide' : 'Any'}</span></td>
      <td style={td}>
        <select style={field} value={row.frequency} disabled={!editable} onChange={e => save({ frequency: e.target.value })}>{FREQUENCIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select>
        {row.frequency === 'weekly' && (
          <div style={{ display: 'flex', gap: 2, marginTop: 3 }}>
            {DAYS.map((d, i) => <button key={d} type="button" disabled={!editable} onClick={() => toggleDay(i)} style={{ fontSize: 9, padding: '2px 4px', borderRadius: 4, border: '1px solid var(--border)', background: (row.days_of_week || []).includes(i) ? 'var(--accent)' : 'var(--bg2)', color: (row.days_of_week || []).includes(i) ? 'var(--white)' : 'var(--text2)', cursor: 'pointer' }}>{d}</button>)}
          </div>
        )}
      </td>
      <td style={{ ...td, textAlign: 'center' }}><input type="checkbox" checked={!!row.required} disabled={!editable} onChange={e => save({ required: e.target.checked })} /></td>
      <td style={td}><input style={field} value={row.instructions || ''} disabled={!editable} placeholder="client-specific instructions" onChange={e => setRow(r => ({ ...r, instructions: e.target.value }))} onBlur={() => row.instructions !== t.instructions && save({})} /></td>
      {editable && <td style={td}><button className="btn btn-ghost btn-sm" onClick={() => onDelete(t.id)} title="Remove">✕</button></td>}
    </tr>
  );
}

function SkillRow({ s, staff, onSave, onDelete, onVerify }) {
  const [row, setRow] = useState(s);
  useEffect(() => setRow(s), [s]);
  const blur = () => { if (JSON.stringify(row) !== JSON.stringify(s)) onSave(row); };
  const date = (k) => <input type="date" style={field} value={row[k] || ''} onChange={e => onSave({ ...row, [k]: e.target.value || null })} />;
  const who = row.caregiver_kind === 'staff' ? row.staff_id || '' : '';
  return (
    <tr>
      <td style={td}><input style={field} value={row.procedure || ''} onChange={e => setRow(r => ({ ...r, procedure: e.target.value }))} onBlur={blur} /><div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>{skillStage(row)}</div></td>
      <td style={td}>
        <select style={field} value={row.caregiver_kind === 'staff' ? `staff:${who}` : 'family'} onChange={e => {
          const v = e.target.value;
          if (v === 'family') onSave({ ...row, caregiver_kind: 'family', staff_id: null });
          else { const id = v.slice(6); const st = staff.find(x => x.id === id); onSave({ ...row, caregiver_kind: 'staff', staff_id: id || null, caregiver_name: st ? `${st.last_name}, ${st.first_name}` : row.caregiver_name }); }
        }}>
          <option value="family">Family caregiver</option>
          <optgroup label="Staff">{staff.map(st => <option key={st.id} value={`staff:${st.id}`}>{st.last_name}, {st.first_name} · {st.position_code}</option>)}</optgroup>
        </select>
        {row.caregiver_kind === 'family' && <input style={{ ...field, marginTop: 3 }} placeholder="name" value={row.caregiver_name || ''} onChange={e => setRow(r => ({ ...r, caregiver_name: e.target.value }))} onBlur={blur} />}
      </td>
      <td style={td}>{date('demonstrated_on')}</td>
      <td style={td}>{date('return_demo_on')}</td>
      <td style={td}>{date('with_assistance_on')}</td>
      <td style={td}>{date('independent_on')}</td>
      <td style={td}>{row.verified_at ? <span style={{ fontSize: 11, color: 'var(--success)' }}>✓ {fmtD(row.verified_at)}</span> : <button className="btn btn-outline btn-sm" style={{ fontSize: 10 }} disabled={!row.independent_on} title={row.independent_on ? 'Sign off as RN' : 'Set the independent date first'} onClick={() => onVerify(row)}>Verify</button>}</td>
      <td style={td}><input style={field} value={row.notes || ''} onChange={e => setRow(r => ({ ...r, notes: e.target.value }))} onBlur={blur} /></td>
      <td style={td}><button className="btn btn-ghost btn-sm" onClick={() => onDelete(s.id)} title="Remove">✕</button></td>
    </tr>
  );
}
